import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { SectionHeader, Button, Loading, EmptyState, Avatar, Tag, Chip, Stars } from "@/components/brand";

const FILTERS = [["", "All"], ["pending", "Pending"], ["approved", "Approved"], ["rejected", "Rejected"]];

export default function AdminTrainers() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("");
  const trainers = useQuery({ queryKey: ["admin", "trainers", status], queryFn: () => api(`/admin/trainers${status ? `?status=${status}` : ""}`) });
  const action = useMutation({ mutationFn: ({ id, act }) => api(`/admin/trainers/${id}/${act}`, { method: "POST" }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin", "trainers"] }); toast.success("Updated"); }, onError: (e) => toast.error(e.message) });

  return (
    <div data-testid="admin-trainers-screen" className="space-y-5">
      <SectionHeader title="Trainers" subtitle="Approve, reject and verify trainers." />
      <div className="flex gap-2">{FILTERS.map(([k, l]) => <Chip key={k || "all"} label={l} selected={status === k} onClick={() => setStatus(k)} testID={`trainer-filter-${k || "all"}`} />)}</div>
      {trainers.isLoading ? <Loading /> : (trainers.data ?? []).length === 0 ? <EmptyState title="No trainers" /> : (
        <div className="space-y-2">
          {trainers.data.map((t) => (
            <div key={t.id} data-testid={`admin-trainer-${t.id}`} className="flex flex-wrap items-center gap-3 p-4 rounded-xl2 border border-divider">
              <Avatar uri={t.avatar} size={48} name={t.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5"><span className="font-display text-base text-ink truncate">{t.name}</span>{t.verified ? <CheckCircle2 size={14} className="text-brand" /> : null}</div>
                <div className="font-body text-xs text-mutedgrey truncate">{t.email} · {t.headline}</div>
                <div className="mt-0.5"><Stars rating={t.rating} size={11} count={t.review_count} /></div>
              </div>
              <Tag label={t.status} tone={t.status === "approved" ? "green" : t.status === "pending" ? "dark" : "grey"} />
              <div className="flex gap-2">
                {t.status !== "approved" ? <Button testID={`approve-${t.id}`} title="Approve" small onClick={() => action.mutate({ id: t.id, act: "approve" })} /> : null}
                {t.status !== "rejected" ? <Button testID={`reject-${t.id}`} title="Reject" small variant="outline" onClick={() => action.mutate({ id: t.id, act: "reject" })} /> : null}
                {!t.verified ? <Button testID={`verify-${t.id}`} title="Verify" small variant="ghost" onClick={() => action.mutate({ id: t.id, act: "verify" })} /> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
